import { useState, useEffect, useCallback } from 'react';

/**
 * A custom React hook that synchronizes state with both window object and localStorage
 * @param key The unique key to store the state under
 * @param initialValue The initial value to use if no stored value exists
 * @returns A tuple containing the current state value and a function to update it
 */
export function useSharedState<T>(
  key: string,
  initialValue: T
): [T, (newValue: T | ((prevValue: T) => T)) => void] {
  // Prefix for both window and localStorage to avoid conflicts
  const windowKey = `windowState_${key}`;

  // Initialize state from window, localStorage, or initial value
  const getInitialState = (): T => {
    // Check if window is defined (for SSR support)
    if (typeof window === 'undefined') {
      return initialValue;
    }

    // First try to get from window
    if ((window as any)[windowKey] !== undefined) {
      return (window as any)[windowKey];
    }

    // Then try to get from localStorage
    try {
      const item = window.localStorage.getItem(windowKey);
      return item ? { ...initialValue, ...JSON.parse(item) } : initialValue;
    } catch (error) {
      console.warn(`Error reading localStorage key "${windowKey}":`, error);
      return initialValue;
    }
  };

  // Using a function in useState to ensure it's only called once during initialization
  const [state, setState] = useState<T>(getInitialState);

  // Handler for storage events from other tabs/windows
  const handleStorageChange = useCallback(
    (event: StorageEvent) => {
      if (event.key === windowKey && event.newValue) {
        try {
          const newValue = JSON.parse(event.newValue);
          setState(newValue);
        } catch (error) {
          console.warn(
            `Error parsing localStorage value for "${windowKey}":`,
            error
          );
        }
      }
    },
    [windowKey]
  );

  // Setup custom event for window property change detection
  useEffect(() => {
    const customEventName = `${windowKey}_changed`;

    // Define property on window with getter/setter to detect changes
    let internalValue = getInitialState();

    // Only define the property if it doesn't exist yet
    if (!(windowKey in window)) {
      Object.defineProperty(window, windowKey, {
        get: () => internalValue,
        set: newValue => {
          internalValue = newValue;

          // Save to localStorage
          try {
            window.localStorage.setItem(windowKey, JSON.stringify(newValue));
          } catch (error) {
            console.warn(
              `Error saving to localStorage key "${windowKey}":`,
              error
            );
          }

          // Dispatch custom event for other components
          window.dispatchEvent(
            new CustomEvent(customEventName, {
              detail: { value: newValue },
            })
          );
        },
        configurable: true,
        enumerable: true,
      });
    }

    // Set initial value if needed and not during render phase
    if ((window as any)[windowKey] === undefined) {
      // Schedule this after rendering is complete
      const timer = setTimeout(() => {
        (window as any)[windowKey] = initialValue;
      }, 0);

      return () => clearTimeout(timer);
    }

    // Listen for custom event
    const handleCustomEvent = (event: Event) => {
      const customEvent = event as CustomEvent;
      setState(customEvent.detail.value);
    };

    // Listen for changes from other tabs/windows
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener(customEventName, handleCustomEvent);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener(customEventName, handleCustomEvent);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowKey, handleStorageChange, initialValue]);

  // Update function that syncs to both window and localStorage
  const updateState = useCallback(
    (newValue: T | ((prevValue: T) => T)) => {
      setState(prevState => {
        const nextValue =
          newValue instanceof Function ? newValue(prevState) : newValue;

        // Use setTimeout to push the window update outside React's render cycle
        setTimeout(() => {
          (window as any)[windowKey] = nextValue;
        }, 0);

        return nextValue;
      });
    },
    [windowKey]
  );

  return [state, updateState];
}
